/**
 * API Configuration - ENV ONLY control
 * Set REACT_APP_API_URL in client/.env - change ONLY this when switching local vs VPS
 * Local:  REACT_APP_API_URL=http://localhost:5002
 * VPS:    REACT_APP_API_URL=https://ubsaapp.testatozas.in
 * Restart client (npm start) after changing .env
 */

const DEFAULT_API_PORT = '5002';

const isLocalDevHost = (host) =>
  host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';

const isPrivateLanHost = (host) =>
  /^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host) || host.endsWith('.local');

const isProductionHost = (host) =>
  host === 'ubsaapp.com' ||
  host === 'www.ubsaapp.com' ||
  host.endsWith('testatozas.in');

const getConfiguredApiPort = () => {
  const envPort = (process.env.REACT_APP_API_PORT || '').trim();
  if (envPort) return envPort;

  const envUrl = (process.env.REACT_APP_API_URL || '').trim();
  const portMatch = envUrl.match(/:(\d+)(?:\/|$)/);
  return portMatch ? portMatch[1] : DEFAULT_API_PORT;
};

const getBaseUrl = () => {
  const envUrl = (process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');

  if (typeof window !== 'undefined') {
    const { hostname, protocol, port, origin } = window.location;
    const normalizedOrigin = origin.replace(/\/$/, '');

    // Local browser dev — always hit local API (ignore stale production env values).
    if (isLocalDevHost(hostname)) {
      if (envUrl && !/^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0)(:\d+)?$/i.test(envUrl)) {
        console.warn('Ignoring non-local REACT_APP_API_URL on localhost:', envUrl);
      }
      return `http://localhost:${getConfiguredApiPort()}`;
    }

    // Production domains — same origin (API served from same host).
    if (isProductionHost(hostname)) {
      return normalizedOrigin;
    }

    // Mobile / tablet on LAN (e.g. 192.168.x.x:3331) — API on same host, backend port.
    if (isPrivateLanHost(hostname)) {
      const apiPort = getConfiguredApiPort();
      if (port && port !== apiPort) {
        return `${protocol}//${hostname}:${apiPort}`;
      }
      return normalizedOrigin;
    }

    // Same-origin deployments (PWA, VPS with unified host).
    if (!port || port === getConfiguredApiPort() || port === '80' || port === '443') {
      return normalizedOrigin;
    }

    return envUrl || normalizedOrigin;
  }

  return envUrl || `http://localhost:${DEFAULT_API_PORT}`;
};

export const API_BASE_URL = getBaseUrl();

export const getApiUrl = (endpoint) => {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  // Always use full URL so we don't rely on proxy (avoids "Proxy error: ECONNREFUSED" when backend is starting)
  return `${API_BASE_URL}${path}`;
};

/** Use for full-page redirects (e.g. OAuth). Always returns full server URL so the browser hits the API server, not the React dev server. */
export const getApiUrlAbsolute = (endpoint) => {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE_URL}${path}`;
};

/** Post-login redirect base — localhost:5002 (API static) should return to React dev on :3000 */
export const getOAuthClientRedirectBase = () => {
  if (typeof window === 'undefined') return 'http://localhost:3000';
  const { hostname, port, protocol } = window.location;
  if (isLocalDevHost(hostname)) {
    if (port === '5002') return 'http://localhost:3000';
    return `${protocol}//${window.location.host}`;
  }
  return window.location.origin;
};

/** Google OAuth must always hit local API on port 5002 when developing on localhost */
export const getGoogleOAuthStartUrl = (redirectPath = '/auth/callback') => {
  const redirectBase = encodeURIComponent(getOAuthClientRedirectBase());
  const path = redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`;
  return getApiUrlAbsolute(
    `/api/auth/google?redirect=${redirectBase}&redirectPath=${encodeURIComponent(path)}`
  );
};

// Export for direct use
export default {
  baseURL: API_BASE_URL,
  getUrl: getApiUrl,

  // Common endpoints
  auth: {
    checkUser: () => getApiUrl('/api/auth/check-user'),
    sendOtp: () => getApiUrl('/api/auth/send-otp'),
    verifyOtp: () => getApiUrl('/api/auth/verify-otp'),
    resendOtp: () => getApiUrl('/api/auth/resend-otp'),
    googleAuth: () => getApiUrl('/api/auth/google'),
    me: () => getApiUrl('/api/auth/me'),
    aadhaarConfig: () => getApiUrl('/api/auth/aadhaar/config'),
    aadhaarSendOtp: () => getApiUrl('/api/auth/aadhaar/send-otp'),
    aadhaarVerifyOtp: () => getApiUrl('/api/auth/aadhaar/verify-otp'),
    aadhaarCheckCompanyVerification: () => getApiUrl('/api/auth/aadhaar/check-company-verification'),
    registerCompany: () => getApiUrl('/api/auth/register-company'),
    companyLogin: () => getApiUrl('/api/auth/company-login'),
  },

  properties: {
    all: () => getApiUrl('/api/properties/all'),
    featured: () => getApiUrl('/api/properties/featured'),
    offers: () => getApiUrl('/api/properties/offers-with-discounts'),
  },

  bookings: {
    create: () => getApiUrl('/api/bookings'),
    list: () => getApiUrl('/api/bookings'),
  },

  notifications: {
    email: () => getApiUrl('/api/notifications/email'),
    push: () => getApiUrl('/api/notifications/push'),
    inApp: () => getApiUrl('/api/notifications/in-app'),
  },
};

// Debug helper
if (process.env.NODE_ENV === 'development') {
  console.log('API Configuration:', {
    baseURL: API_BASE_URL,
    nodeEnv: process.env.NODE_ENV,
  });
}
